// Add this specific import to avoid random error when initialized date picker
import 'react-dates/initialize';
import { DateRangePicker } from 'react-dates';
import i18next from 'i18next';
import moment from 'moment-timezone';
import MomentPropTypes from 'react-moment-proptypes';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';

import { DATE_DISPLAY_FORMATS, DATE_PICKER_DOT_COLOR } from '../constants';
import { DatePickerInputIcon } from '../DatePickerIcon';
import { renderCustomArrowIcon, renderCustomNavIconLeft, renderCustomNavIconRight } from '../utils';
import { renderDatePickerModalInfo } from '../DatePickerModalInformation';
import DatePickerDay from '../DatePickerDay';
import Text, { ENUM_COLORS } from '../../Text';

import { Container, PresetsContainer, PresetButton } from './styledComponents';

// Default preset configurations
export const DEFAULT_PRESETS = [
  {
    id: 'last-7-days',
    label: 'GENERAL.DATE_PICKER_PRESET_LAST_7_DAYS',
    getDates: (timezone) => {
      const startDate = moment.tz(timezone).subtract(7, 'days').startOf('day');
      const endDate = moment.tz(timezone).subtract(1, 'day').endOf('day');
      return { startDate, endDate };
    },
  },
  {
    id: 'last-14-days',
    label: 'GENERAL.DATE_PICKER_PRESET_LAST_14_DAYS',
    getDates: (timezone) => {
      const startDate = moment.tz(timezone).subtract(14, 'days').startOf('day');
      const endDate = moment.tz(timezone).subtract(1, 'day').endOf('day');
      return { startDate, endDate };
    },
  },
  {
    id: 'last-30-days',
    label: 'GENERAL.DATE_PICKER_PRESET_LAST_30_DAYS',
    getDates: (timezone) => {
      const startDate = moment.tz(timezone).subtract(30, 'days').startOf('day');
      const endDate = moment.tz(timezone).subtract(1, 'day').endOf('day');
      return { startDate, endDate };
    },
  },
];

/**
 * A period DatePicker with customizable presets.
 */

export const PeriodDatePicker = (props) => {
  const {
    startDate,
    endDate,
    onDatesChange,
    displayFormat,
    authorizedDates,
    warnedDates,
    forbiddenDates,
    maxFutureDate,
    maxPastDate,
    disabled,
    calendarInfo,
    customStyle,
    onNextMonthClick,
    onPrevMonthClick,
    focusedDateInput,
    setFocusedDateInput,
    minimumNights,
    timezone,
    presets,
    onPresetSelect,
    numberOfMonths,
  } = props;

  const [selectedPreset, setSelectedPreset] = useState(null);

  // Reset selected preset when dates are changed manually (not through preset selection)
  useEffect(() => {
    if (!selectedPreset) return;

    if (!startDate || !endDate) {
      setSelectedPreset(null);
      return;
    }

    // Check if current dates match the selected preset
    const selectedPresetConfig = presets.find((p) => p.id === selectedPreset);
    if (selectedPresetConfig) {
      const { startDate: presetStartDate, endDate: presetEndDate } =
        selectedPresetConfig.getDates(timezone);

      // If dates don't match the preset, it means they were changed manually
      if (!startDate.isSame(presetStartDate, 'day') || !endDate.isSame(presetEndDate, 'day')) {
        setSelectedPreset(null);
      }
    }
  }, [startDate, endDate, presets, timezone]);

  const handlePresetSelect = async (preset) => {
    try {
      const { startDate: presetStartDate, endDate: presetEndDate } = preset.getDates(timezone);
      setSelectedPreset(preset.id);

      // // Clear any focused input to ensure both dates are set properly
      // setFocusedDateInput(null);

      if (onPresetSelect) {
        await onPresetSelect(preset, { startDate: presetStartDate, endDate: presetEndDate });
      }

      // Set both dates at once to ensure they're both updated
      await onDatesChange({ startDate: presetStartDate, endDate: presetEndDate }, true);
    } catch (error) {
      console.error('Error applying preset:', error);
    }
  };

  const isOutsideRange = (day) => {
    const currentDay = day.clone();

    if (
      forbiddenDates.some((forbiddenDay) =>
        currentDay.startOf('day').isSame(forbiddenDay.startOf('day')),
      )
    ) {
      return true;
    }

    if (maxPastDate && !maxFutureDate) {
      const maxPast = maxPastDate.clone();
      return currentDay.startOf('day').isBefore(maxPast.startOf('day'));
    }

    if (!maxPastDate && maxFutureDate) {
      const maxFuture = maxFutureDate.clone();
      return currentDay.startOf('day').isAfter(maxFuture.endOf('day'));
    }

    if (maxPastDate && maxFutureDate) {
      const maxPast = maxPastDate.clone();
      const maxFuture = maxFutureDate.clone();
      return !currentDay
        .startOf('day')
        .isBetween(maxPast.subtract(1, 'days').endOf('day'), maxFuture.endOf('day'));
    }

    return false;
  };

  const renderDay = (day) => {
    const currentDay = day.clone();

    const isToday = currentDay.startOf('day').isSame(moment.tz(timezone).startOf('day'));

    if (
      forbiddenDates.some((forbiddenDay) =>
        currentDay.startOf('day').isSame(forbiddenDay.startOf('day')),
      )
    ) {
      return (
        <DatePickerDay
          color={DATE_PICKER_DOT_COLOR.RED}
          day={currentDay}
          hasCircle={isToday}
          hasDot={true}
        />
      );
    }

    if (
      warnedDates.some((warnedDay) => currentDay.startOf('day').isSame(warnedDay.startOf('day')))
    ) {
      return (
        <DatePickerDay
          color={DATE_PICKER_DOT_COLOR.ORANGE}
          day={currentDay}
          hasCircle={isToday}
          hasDot={true}
        />
      );
    }

    if (
      authorizedDates.some((authorizedDay) =>
        currentDay.startOf('day').isSame(authorizedDay.startOf('day')),
      )
    ) {
      return (
        <DatePickerDay
          color={DATE_PICKER_DOT_COLOR.GREEN}
          day={currentDay}
          hasCircle={isToday}
          hasDot={true}
        />
      );
    }

    return <DatePickerDay day={currentDay} hasCircle={isToday} />;
  };

  const renderCalendarInfoWithPresets = () => {
    const originalCalendarInfo = calendarInfo
      ? renderDatePickerModalInfo(calendarInfo, timezone)
      : null;

    if (!presets.length) {
      return originalCalendarInfo;
    }

    return (
      <div>
        <PresetsContainer>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {presets.map((preset) => (
              <PresetButton
                disabled={disabled}
                isSelected={selectedPreset === preset.id}
                key={preset.id}
                onClick={() => handlePresetSelect(preset)}
              >
                <Text
                  color={selectedPreset === preset.id ? ENUM_COLORS.LIGHTEST : ENUM_COLORS.DARKEST}
                  style={{ fontSize: '12px', fontWeight: '500' }}
                >
                  {i18next.t(preset.label) || preset.label}
                </Text>
              </PresetButton>
            ))}
          </div>
        </PresetsContainer>
        {originalCalendarInfo}
      </div>
    );
  };

  return (
    <Container
      calendarInfo={!!(calendarInfo || presets.length)}
      disabled={disabled}
      focused={focusedDateInput}
      style={customStyle}
    >
      <DateRangePicker
        calendarInfoPosition={'top'}
        customArrowIcon={renderCustomArrowIcon()}
        customInputIcon={DatePickerInputIcon(startDate)}
        disabled={disabled}
        displayFormat={displayFormat}
        endDate={endDate}
        endDateId="your_unique_end_date_id"
        endDatePlaceholderText={i18next.t('GENERAL.ANALYTICS_HEADER_DATE_PICKER_END_PLACEHOLDER')}
        focusedInput={focusedDateInput}
        hideKeyboardShortcutsPanel={true}
        isOutsideRange={(day) => isOutsideRange(day)}
        minimumNights={minimumNights || 0}
        navNext={renderCustomNavIconRight()}
        navPrev={renderCustomNavIconLeft()}
        noBorder={true}
        numberOfMonths={numberOfMonths}
        readOnly={true}
        renderCalendarInfo={renderCalendarInfoWithPresets}
        renderDayContents={(day) => renderDay(day)}
        startDate={startDate}
        startDateId="your_unique_start_date_id"
        startDatePlaceholderText={i18next.t(
          'GENERAL.ANALYTICS_HEADER_DATE_PICKER_START_PLACEHOLDER',
        )}
        onDatesChange={(dates) => onDatesChange(dates)}
        onFocusChange={(focusedDateInput) => setFocusedDateInput(focusedDateInput)}
        onNextMonthClick={onNextMonthClick}
        onPrevMonthClick={onPrevMonthClick}
      />
    </Container>
  );
};

PeriodDatePicker.propTypes = {
  startDate: MomentPropTypes.momentObj, // current selected date
  endDate: MomentPropTypes.momentObj, // current selected date
  disabled: PropTypes.bool, // is the datepicker disabled
  displayFormat: PropTypes.oneOf(Object.values(DATE_DISPLAY_FORMATS)), // date format in input
  forbiddenDates: PropTypes.arrayOf(MomentPropTypes.momentObj), // Array of date that are forbidden to be selected (have a red dot)
  warnedDates: PropTypes.arrayOf(MomentPropTypes.momentObj), // Array of date that are warned to be selected (have a orange dot)
  authorizedDates: PropTypes.arrayOf(MomentPropTypes.momentObj), // Array of date that are authorized to be selected (have a green dot)
  maxFutureDate: MomentPropTypes.momentObj, // maximum selectable date in the Future
  maxPastDate: MomentPropTypes.momentObj, // maximum selectable date in the Past
  onDatesChange: PropTypes.func.isRequired, // function called when date changes
  startDatePlaceholderText: PropTypes.string, // Placeholder to display in input
  endDatePlaceholderText: PropTypes.string, // Placeholder to display in input
  calendarInfo: PropTypes.shape({
    dotsInfomations: PropTypes.arrayOf(
      PropTypes.shape({
        dotColor: PropTypes.oneOf(Object.values(DATE_PICKER_DOT_COLOR)),
        dotInfo: PropTypes.string,
      }), // array of object {dotColor, dotInfo} which contains information display for a dot color
    ),
    globalInfo: PropTypes.string, // A string containing a global information on datePicker
    //renderTooltip: PropTypes.func, // TO BE IMPLEMENTED function which return a tooltip content
  }), // Object containing information to display in modal
  onNextMonthClick: PropTypes.func, // function called when switch to next month
  onPrevMonthClick: PropTypes.func, // function called when switch to previous month
  customStyle: PropTypes.shape(), // an object containing specific style for datePicker
  focusedDateInput: PropTypes.string, // FocusedInputShape from react-dates library ('startDate' | 'endDate')
  setFocusedDateInput: PropTypes.func, // function called when focused date input change
  minimumNights: PropTypes.number, // Number of minimum night. If 0, we can select same date for startDate and endDate
  timezone: PropTypes.string.isRequired,
  presets: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      getDates: PropTypes.func.isRequired, // function that returns { startDate, endDate }
    }),
  ), // Array of custom preset configurations
  onPresetSelect: PropTypes.func, // Callback when a preset is selected
  numberOfMonths: PropTypes.number, // Number of months displayed at once (use 1 in narrow containers like a drawer)
};

PeriodDatePicker.defaultProps = {
  date: null,
  startDate: null,
  endDate: null,
  customStyle: {},
  disabled: false,
  displayFormat: DATE_DISPLAY_FORMATS.CONDENSED_DAY_DATE_MONTH,
  forbiddenDates: [],
  warnedDates: [],
  authorizedDates: [],
  maxFutureDate: null,
  maxPastDate: null,
  placeholder: i18next.t('GENERAL.DATEPICKER_PLACEHOLDER'),
  calendarInfo: null,
  timezone: 'Europe/Paris',
  presets: [],
  onPresetSelect: null,
  numberOfMonths: 2,
};
